/**
 * PDP: With Name / Without Name variant sync (Janamaz and similar products).
 *
 * Customeow either paints its own Customize CTA (Buttons block — hides theme ATC)
 * or hooks the theme [name="add"] button (assign mode). Never force the theme ATC
 * visible while Customeow is active — that covers their desktop CTA and breaks clicks.
 */
(function () {
  'use strict';

  const VARIANT_UPDATE_EVENT =
    (typeof window.MainEvents !== 'undefined' && window.MainEvents.variantUpdate) || 'variant:update';

  let syncTimer = null;
  const syncTimersLate = [];
  let initRetriesScheduled = false;
  let customeowInitScheduled = false;

  function normalizeValue(value) {
    return (value || '')
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  function isWithoutNameValue(value) {
    const normalized = normalizeValue(value);
    if (!normalized) return false;
    return (
      normalized === 'without name' ||
      normalized === 'no name' ||
      normalized === 'without personalization' ||
      normalized === 'without personalisation' ||
      normalized === 'non personalized' ||
      normalized === 'non personalised' ||
      normalized === 'no personalization' ||
      normalized === 'no personalisation' ||
      normalized === 'without custom' ||
      normalized === 'no custom' ||
      normalized === 'not customized' ||
      normalized === 'not customised' ||
      normalized === 'plain' ||
      normalized === 'standard' ||
      normalized === 'blank' ||
      normalized === 'none' ||
      normalized.includes('without name') ||
      normalized.includes('no name') ||
      normalized.includes('without personal') ||
      normalized.includes('non personal')
    );
  }

  function isWithNameValue(value) {
    const normalized = normalizeValue(value);
    if (!normalized) return false;
    if (isWithoutNameValue(normalized)) return false;
    return (
      normalized === 'with name' ||
      normalized === 'with personalization' ||
      normalized === 'with personalisation' ||
      normalized === 'personalized' ||
      normalized === 'personalised' ||
      normalized === 'customize' ||
      normalized === 'customise' ||
      normalized === 'customized' ||
      normalized === 'customised' ||
      normalized === 'with custom' ||
      normalized.endsWith('with name') ||
      normalized.includes('with personal') ||
      normalized.startsWith('personaliz') ||
      normalized.startsWith('personalisation') ||
      normalized.startsWith('customiz') ||
      normalized.startsWith('customis')
    );
  }

  function getProductInfo() {
    return document.querySelector('product-info');
  }

  function getMainVariantSelects() {
    const productInfo = getProductInfo();
    return (
      productInfo?.querySelector('variant-selects[data-context="main"]') ||
      productInfo?.querySelector('variant-selects') ||
      document.querySelector('variant-selects[data-context="main"]') ||
      document.querySelector('product-info variant-selects')
    );
  }

  function getOptionWrappers(variantSelects) {
    if (!variantSelects) return [];

    return Array.from(
      variantSelects.querySelectorAll('fieldset.product-form__input, .product-form__input.variant-option')
    );
  }

  function getWrapperValues(wrapper) {
    const values = new Set();

    wrapper.querySelectorAll('input[type="radio"]').forEach(function (input) {
      if (input.value) values.add(normalizeValue(input.value));
    });

    // Read every dropdown choice so a With/Without Name selector is recognized;
    // looking only at its selected option can never prove that both sides exist.
    wrapper.querySelectorAll('option').forEach(function (option) {
      if (option.value) values.add(normalizeValue(option.value));
    });

    const select = wrapper.querySelector('select');
    if (select?.value) values.add(normalizeValue(select.value));

    return values;
  }

  function wrapperLooksLikeNameToggle(wrapper) {
    const values = getWrapperValues(wrapper);
    let hasWith = false;
    let hasWithout = false;

    values.forEach(function (value) {
      if (isWithNameValue(value)) hasWith = true;
      if (isWithoutNameValue(value)) hasWithout = true;
    });

    // Only treat as a name/custom toggle when BOTH sides are present.
    // (Avoid false matches on options like Color / Size that mention "name".)
    return hasWith && hasWithout;
  }

  function findNameOptionWrapper(variantSelects) {
    return getOptionWrappers(variantSelects).find(wrapperLooksLikeNameToggle);
  }

  function productHasNameToggle(variantSelects) {
    return Boolean(findNameOptionWrapper(variantSelects));
  }

  function isWithNameSelected(variantSelects) {
    const nameWrapper = findNameOptionWrapper(variantSelects);
    if (!nameWrapper) return true;

    const checked =
      nameWrapper.querySelector('input[type="radio"]:checked') ||
      nameWrapper.querySelector('input[type="radio"][checked]');
    if (checked) return isWithNameValue(checked.value);

    const select = nameWrapper.querySelector('select');
    if (select?.value) return isWithNameValue(select.value);

    const selectedLabel = nameWrapper.querySelector('[data-selected-value]');
    if (selectedLabel?.textContent) return isWithNameValue(selectedLabel.textContent);

    return true;
  }

  function getSelectedVariantFromDom(variantSelects) {
    const script = variantSelects?.querySelector('[data-selected-variant]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function updateSkuDisplay(sku, scope) {
    const root = scope || document;

    root.querySelectorAll('[data-sku] [data-sku-value], [data-sku] #sku-value').forEach(function (el) {
      el.textContent = sku || 'N/A';
    });

    root.querySelectorAll('[data-sku]').forEach(function (skuItem) {
      skuItem.style.display = sku ? '' : 'none';
    });
  }

  function getSubmitButtons(productInfo) {
    const root = productInfo || document;
    return Array.from(root.querySelectorAll('.product-form__buttons [name="add"]')).filter(function (btn) {
      return !btn.closest('[data-type="customeowApp"]');
    });
  }

  function showButtonContainers(productInfo) {
    const root = productInfo || document;

    root
      .querySelectorAll(
        '.product-form__buttons div[data-product-variant-media].add-to-cart-button-container, .product-form__buttons > .add-to-cart-button-container, .product-form__buttons .add-to-cart-button-inner'
      )
      .forEach(function (el) {
        el.style.setProperty('display', 'flex', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('width', '100%', 'important');
        el.hidden = false;
        el.removeAttribute('hidden');
      });
  }

  function setThemeCustomizePriority(productInfo, active) {
    const root = productInfo || document;
    root
      .querySelectorAll(
        '.product-form__buttons div[data-product-variant-media].add-to-cart-button-container, .product-form__buttons > .add-to-cart-button-container, .product-form__buttons .add-to-cart-button-inner'
      )
      .forEach(function (el) {
        if (active) {
          el.style.setProperty('position', 'relative', 'important');
          el.style.setProperty('z-index', '6', 'important');
          el.dataset.themeCustomizePriority = 'true';
          return;
        }
        if (el.dataset.themeCustomizePriority !== 'true') return;
        el.style.removeProperty('position');
        el.style.removeProperty('z-index');
        delete el.dataset.themeCustomizePriority;
      });
  }

  function hasActiveCustomeow(productInfo) {
    const root = productInfo || document;
    const apps = root.querySelectorAll
      ? root.querySelectorAll('[data-type="customeowApp"]')
      : [];
    for (let i = 0; i < apps.length; i++) {
      const app = apps[i];
      if (app.dataset.themeForcedHide === 'true') continue;
      if (app.hidden) continue;
      const style = window.getComputedStyle(app);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      return true;
    }

    // Presence fallback only if a shell exists that we have not intentionally hidden.
    const all = document.querySelectorAll('[data-type="customeowApp"]');
    for (let i = 0; i < all.length; i++) {
      if (all[i].dataset.themeForcedHide === 'true') continue;
      return true;
    }
    return false;
  }

  function restoreCustomeowApps(productInfo) {
    const root = productInfo || document;
    root.querySelectorAll('[data-type="customeowApp"]').forEach(function (el) {
      if (el.dataset.themeForcedHide === 'true') {
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('pointer-events');
        el.hidden = false;
        delete el.dataset.themeForcedHide;
      }
    });
  }

  function forceShowSubmitButtons(productInfo) {
    if (!productInfo) return;

    showButtonContainers(productInfo);

    // Customeow Buttons block hides theme [name=add] on purpose. Forcing it back
    // with !important puts it on top of Customeow's desktop CTA and eats clicks.
    // Only yield when Customeow is actually active AND this SKU is connected.
    if (hasActiveCustomeow(productInfo) && isSkuConnectedToCustomeow(productInfo)) {
      clearForcedSubmitStyles(productInfo);
      restoreCustomeowApps(productInfo);
      yieldThemeAtcToCustomeow(productInfo);
      return;
    }

    releaseThemeAtcFromCustomeow(productInfo);

    getSubmitButtons(productInfo).forEach(function (btn) {
      btn.style.setProperty('display', 'flex', 'important');
      btn.style.setProperty('visibility', 'visible', 'important');
      btn.style.setProperty('opacity', '1', 'important');
      btn.style.setProperty('width', '100%', 'important');
      btn.style.setProperty('pointer-events', 'auto', 'important');
      btn.hidden = false;
      btn.removeAttribute('hidden');
      btn.removeAttribute('aria-hidden');
      btn.dataset.themeForcedShow = 'true';
      delete btn.dataset.themeHiddenForCustomeow;
    });

    document.querySelectorAll('sticky-atc [name="add"], sticky-atc .product-form__submit').forEach(function (btn) {
      btn.style.setProperty('display', 'flex', 'important');
      btn.style.setProperty('visibility', 'visible', 'important');
      btn.style.setProperty('pointer-events', 'auto', 'important');
      btn.hidden = false;
    });
  }

  function hideCustomeowOverlay(productInfo) {
    const root = productInfo || document;

    root.querySelectorAll('[data-type="customeowApp"]').forEach(function (el) {
      el.style.setProperty('display', 'none', 'important');
      el.hidden = true;
      el.dataset.themeForcedHide = 'true';
    });
  }

  /**
   * Only undo styles THIS script forced visible (Without Name / forceShow).
   * Blindly removeProperty('display') was undoing Customeow's own display:none
   * on the theme ATC every release tick — first Customize clicks hit a dead button.
   */
  function clearForcedSubmitStyles(productInfo) {
    getSubmitButtons(productInfo).forEach(function (btn) {
      if (btn.dataset.themeHiddenForCustomeow === 'true') return;
      if (btn.dataset.themeForcedShow !== 'true') return;

      btn.style.removeProperty('display');
      btn.style.removeProperty('visibility');
      btn.style.removeProperty('opacity');
      btn.style.removeProperty('width');
      btn.style.removeProperty('pointer-events');
      btn.style.removeProperty('z-index');
      delete btn.dataset.themeForcedShow;
    });
  }

  function hideThemeAtcForCustomeow(productInfo) {
    getSubmitButtons(productInfo).forEach(function (btn) {
      btn.style.setProperty('display', 'none', 'important');
      btn.style.setProperty('pointer-events', 'none', 'important');
      btn.setAttribute('aria-hidden', 'true');
      btn.tabIndex = -1;
      btn.dataset.themeHiddenForCustomeow = 'true';
      delete btn.dataset.themeForcedShow;
    });
  }

  /** Undo Customeow yield so theme Add to cart is clickable again (one tap). */
  function releaseThemeAtcFromCustomeow(productInfo) {
    getSubmitButtons(productInfo).forEach(function (btn) {
      if (btn.dataset.themeHiddenForCustomeow !== 'true') return;
      btn.style.removeProperty('display');
      btn.style.removeProperty('pointer-events');
      btn.style.removeProperty('visibility');
      btn.style.removeProperty('opacity');
      btn.style.removeProperty('z-index');
      btn.removeAttribute('aria-hidden');
      if (btn.tabIndex < 0) btn.tabIndex = 0;
      delete btn.dataset.themeHiddenForCustomeow;
    });
  }

  function isElementInteractable(el) {
    if (!el || el.disabled) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    if (el.hidden) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') {
      return false;
    }
    const rect = el.getBoundingClientRect();
    return rect.width >= 2 && rect.height >= 2;
  }

  function collectDeepCandidates(root) {
    const list = [];
    const visit = function (node) {
      if (!node || !node.querySelectorAll) return;
      node
        .querySelectorAll('button, a[href], [role="button"], input[type="button"], input[type="submit"], [class*="customeow"], [class*="Customeow"]')
        .forEach(function (el) {
          list.push(el);
        });
      node.querySelectorAll('*').forEach(function (el) {
        if (el.shadowRoot) visit(el.shadowRoot);
      });
    };
    visit(root);
    return list;
  }

  /**
   * Customeow Buttons block renders its own CTA inside [data-type="customeowApp"].
   * Assign mode hooks the theme [name=add] and has no separate CTA.
   * @param {ParentNode} [scope]
   * @param {{ labeledOnly?: boolean }} [options]
   */
  function findCustomeowCta(scope, options) {
    const labeledOnly = Boolean(options && options.labeledOnly);
    const root = scope || document;
    const apps = [];
    if (root.matches && root.matches('[data-type="customeowApp"]')) {
      apps.push(root);
    } else {
      root.querySelectorAll('[data-type="customeowApp"]').forEach(function (app) {
        apps.push(app);
      });
    }
    const labeled = [];
    const unlabeled = [];

    for (let i = 0; i < apps.length; i++) {
      const app = apps[i];
      if (app.dataset.themeForcedHide === 'true' || app.hidden) continue;
      const appStyle = window.getComputedStyle(app);
      if (appStyle.display === 'none' || appStyle.visibility === 'hidden') continue;

      const candidates = collectDeepCandidates(app);

      for (let j = 0; j < candidates.length; j++) {
        const el = candidates[j];
        if (el.matches && el.matches('[name="add"]') && !app.contains(el)) continue;
        if (!isElementInteractable(el)) continue;

        const text = (el.textContent || el.value || el.getAttribute('aria-label') || '')
          .trim()
          .toLowerCase();
        if (textLooksLikeUnavailableCustomization(text)) continue;
        if (
          text.includes('customiz') ||
          text.includes('customis') ||
          text.includes('personaliz') ||
          text.includes('personalis') ||
          text.includes('design') ||
          text.includes('add to cart')
        ) {
          labeled.push(el);
        } else {
          unlabeled.push(el);
        }
      }
    }

    if (labeledOnly) return labeled[0] || null;
    return labeled[0] || unlabeled[0] || null;
  }

  function isLikelyPrimaryCta(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.width >= 100 && rect.height >= 32;
  }

  function customeowButtonsBlockReady(scope) {
    const root = scope || document;
    const apps = [];
    if (root.matches && root.matches('[data-type="customeowApp"]')) {
      apps.push(root);
    }
    root.querySelectorAll('[data-type="customeowApp"]').forEach(function (app) {
      apps.push(app);
    });

    for (let i = 0; i < apps.length; i++) {
      const app = apps[i];
      if (app.dataset.themeForcedHide === 'true' || app.hidden) continue;
      const appStyle = window.getComputedStyle(app);
      if (appStyle.display === 'none' || appStyle.visibility === 'hidden') continue;

      const candidates = collectDeepCandidates(app);
      for (let j = 0; j < candidates.length; j++) {
        const candidate = candidates[j];
        if (!isElementInteractable(candidate)) continue;
        const candidateText = (
          candidate.textContent ||
          candidate.value ||
          candidate.getAttribute?.('aria-label') ||
          ''
        ).trim();
        if (textLooksLikeUnavailableCustomization(candidateText)) continue;
        if (textLooksLikeCustomize(candidateText) || textLooksLikeAddToCart(candidateText)) return true;
      }

      const rect = app.getBoundingClientRect();
      const text = (app.textContent || '').trim().toLowerCase();
      if (
        rect.width >= 100 &&
        rect.height >= 36 &&
        !textLooksLikeUnavailableCustomization(text) &&
        (text.includes('customiz') ||
          text.includes('customis') ||
          text.includes('personaliz') ||
          text.includes('add to cart'))
      ) {
        return true;
      }
    }
    return false;
  }

  function activateCustomeowOnce(el) {
    if (!el || typeof el.click !== 'function') return false;
    el.click();
    return true;
  }

  function resolveCustomeowCta(scope) {
    const labeled = findCustomeowCta(scope, { labeledOnly: true });
    if (labeled) return labeled;
    const fallback = findCustomeowCta(scope);
    if (fallback && isLikelyPrimaryCta(fallback)) return fallback;
    return null;
  }

  function textLooksLikeCustomize(text) {
    const value = (text || '').toLowerCase();
    if (textLooksLikeUnavailableCustomization(value)) return false;
    return (
      value.includes('customiz') ||
      value.includes('customis') ||
      value.includes('personaliz') ||
      value.includes('personalis') ||
      value.includes('design now') ||
      value.includes('start design')
    );
  }

  function textLooksLikeUnavailableCustomization(text) {
    const value = (text || '').toLowerCase();
    const mentionsCustomization =
      value.includes('customiz') ||
      value.includes('customis') ||
      value.includes('personaliz') ||
      value.includes('personalis');
    if (!mentionsCustomization) return false;

    return (
      value.includes('not available') ||
      value.includes('unavailable') ||
      value.includes('not supported') ||
      value.includes('cannot be customized') ||
      value.includes('cannot be customised') ||
      value.includes("can't be customized") ||
      value.includes("can't be customised")
    );
  }

  function textLooksLikeAddToCart(text) {
    const value = (text || '').toLowerCase();
    return (
      value.includes('add to cart') ||
      value.includes('add to bag') ||
      value.includes('buy it now') ||
      value.includes('buy now') ||
      value === 'sold out' ||
      value === 'unavailable'
    );
  }

  function getCurrentVariantKey() {
    const variant = getSelectedVariantFromDom(getMainVariantSelects());
    const formVariantId = getProductInfo()?.querySelector?.('form[action*="/cart/add"] [name="id"]')?.value;
    return String(formVariantId || variant?.id || variant?.sku || '');
  }

  const CUSTOMEOW_CONFIG_TIMEOUT_MS = 6500;
  const CUSTOMEOW_CONFIG_RETRY_MS = 15000;
  const customeowConfigurationStates = new Map();
  let labelApplyTimer = null;

  function getCustomeowServerConfig() {
    try {
      const configFromModule = window.getCustomeowFuns?.('serverConfig');
      if (configFromModule) return configFromModule;
    } catch (e) {}
    return window._customeowDebug?.serverConfig || null;
  }

  function getCustomeowVariant(serverConfig, preferredVariant) {
    const variants = serverConfig?.product?.variants || [];
    const preferredId = preferredVariant?.id;
    if (preferredId != null) {
      const preferredMatch = variants.find(function (variant) {
        return String(variant.id) === String(preferredId);
      });
      if (preferredMatch) return preferredMatch;
    }

    const domVariantId = getCurrentVariantKey();
    if (domVariantId) {
      const domMatch = variants.find(function (variant) {
        return String(variant.id) === domVariantId;
      });
      if (domMatch) return domMatch;
    }

    const selectedVariant = getSelectedVariantFromDom(getMainVariantSelects());
    if (selectedVariant?.id != null) {
      const selectedMatch = variants.find(function (variant) {
        return String(variant.id) === String(selectedVariant.id);
      });
      if (selectedMatch) return selectedMatch;
    }

    return preferredVariant || selectedVariant || variants[0] || null;
  }

  function buildCustomeowConfigurationUrl(serverConfig, sku) {
    const shopConfig = serverConfig.shopConfig || {};
    const productMetafields = serverConfig.productMetafields || {};
    const env = shopConfig.env === 'production' ? 'prod' : shopConfig.env;
    const params = {
      platform: serverConfig.platform || 'shopify',
      sku: sku,
      shopId: serverConfig.shopId,
      env: env,
      storeVersion: shopConfig.storeVersion,
      productVersion: productMetafields.productVersion,
      productIntegrationType: productMetafields.productIntegrationType,
      productId: serverConfig.product?.id,
    };

    try {
      const customeowUrlBuilder = window.getCustomeowFuns?.('getCustomeowIntegrated');
      if (typeof customeowUrlBuilder === 'function') return customeowUrlBuilder(params);
    } catch (e) {}

    const origins = {
      dev: 'https://sunzi-cn.oss-cn-hangzhou.aliyuncs.com',
      beta: 'https://sunzi-cn.oss-cn-hangzhou.aliyuncs.com',
      uat: 'https://sunzi-us-east.oss-us-east-1.aliyuncs.com',
      prod: 'https://json.staticmeow.com',
    };
    const origin = origins[env] || origins.prod;
    const namespace = !env || env === 'prod' ? 'customeow' : `customeow-${env}`;
    const platform = encodeURIComponent(params.platform);
    const shopId = encodeURIComponent(String(params.shopId || ''));
    let path = `${origin}/${namespace}/app/${platform}/${shopId}/${encodeURIComponent(String(sku))}.json`;
    if (productMetafields.productIntegrationType === 1) {
      path = `${origin}/${namespace}/app/${platform}/${shopId}/product/${encodeURIComponent(
        String(serverConfig.product?.id || '')
      )}.json`;
    }

    const versions = new URLSearchParams();
    if (productMetafields.productVersion) versions.set('productVersion', productMetafields.productVersion);
    if (shopConfig.storeVersion) versions.set('storeVersion', shopConfig.storeVersion);
    const query = versions.toString();
    return query ? `${path}?${query}` : path;
  }

  function getCustomeowConfigurationTarget(preferredVariant) {
    const serverConfig = getCustomeowServerConfig();
    if (!serverConfig?.product || !serverConfig.shopId) return null;

    const variant = getCustomeowVariant(serverConfig, preferredVariant);
    const productMetafields = serverConfig.productMetafields || {};
    const isProductIntegration = productMetafields.productIntegrationType === 1;
    const isPod = serverConfig.isPod === true;
    const sku = isProductIntegration || isPod ? String(serverConfig.product.id) : String(variant?.sku || '');
    if (!sku) return null;

    if (isPod) {
      return {
        key: `pod:${serverConfig.shopId}:${serverConfig.product.id}:${variant?.id || ''}`,
        isPod: true,
        serverConfig: serverConfig,
        sku: sku,
        variant: variant,
      };
    }

    const url = buildCustomeowConfigurationUrl(serverConfig, sku);
    return {
      key: url,
      isPod: false,
      serverConfig: serverConfig,
      sku: sku,
      url: url,
      variant: variant,
    };
  }

  function isValidCustomeowConfiguration(data) {
    return Boolean(
      data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        (String(data.customizerId || '').trim() || String(data.customizerVersion || '').trim())
    );
  }

  function hasRenderedCustomeowConfiguration(target) {
    if (!target?.isPod) return false;
    return Boolean(window._cacheSku?.[String(target.serverConfig.product.id)]);
  }

  function syncUiAfterCustomeowConfiguration(target) {
    const currentTarget = getCustomeowConfigurationTarget();
    if (!target || !currentTarget || currentTarget.key !== target.key) return;
    window.queueMicrotask(function () {
      const latestTarget = getCustomeowConfigurationTarget();
      if (!latestTarget || latestTarget.key !== target.key) return;
      applyNameToggleState(getSelectedVariantFromDom(getMainVariantSelects()));
    });
  }

  function setCustomeowConfigurationState(target, status, details) {
    const previous = customeowConfigurationStates.get(target.key) || {};
    if (previous.retryTimer && status !== 'fallback') window.clearTimeout(previous.retryTimer);
    const next = Object.assign({}, previous, details || {}, {
      status: status,
      target: target,
      promise: null,
    });
    customeowConfigurationStates.set(target.key, next);
    syncUiAfterCustomeowConfiguration(target);
    return next;
  }

  function scheduleCustomeowConfigurationRetry(target, state) {
    if (!state || state.retryTimer || state.attempts >= 2) return;
    state.retryTimer = window.setTimeout(function () {
      state.retryTimer = null;
      const currentTarget = getCustomeowConfigurationTarget();
      if (!currentTarget || currentTarget.key !== target.key) return;
      ensureCustomeowConfiguration(target.variant, { force: true });
    }, CUSTOMEOW_CONFIG_RETRY_MS);
  }

  function waitForPodCustomeowConfiguration(target) {
    return new Promise(function (resolve) {
      const startedAt = Date.now();
      const timer = window.setInterval(function () {
        if (hasRenderedCustomeowConfiguration(target)) {
          window.clearInterval(timer);
          resolve(true);
          return;
        }
        if (Date.now() - startedAt >= CUSTOMEOW_CONFIG_TIMEOUT_MS) {
          window.clearInterval(timer);
          resolve(false);
        }
      }, 150);
    });
  }

  function ensureCustomeowConfiguration(preferredVariant, options) {
    const target = getCustomeowConfigurationTarget(preferredVariant);
    if (!target) return Promise.resolve(false);

    const existing = customeowConfigurationStates.get(target.key);
    if (existing?.status === 'checking' && existing.promise) return existing.promise;
    if (!options?.force) {
      if (existing?.status === 'connected') return Promise.resolve(true);
      if (existing?.status === 'unconfigured') return Promise.resolve(false);
      if (existing?.status === 'fallback' && existing.retryAt > Date.now()) return Promise.resolve(false);
    }

    const state = {
      status: 'checking',
      target: target,
      promise: null,
      attempts: (existing?.attempts || 0) + 1,
      retryTimer: existing?.retryTimer || null,
    };
    customeowConfigurationStates.set(target.key, state);

    if (target.isPod) {
      state.promise = waitForPodCustomeowConfiguration(target).then(function (connected) {
        if (connected) {
          setCustomeowConfigurationState(target, 'connected', { attempts: state.attempts });
          return true;
        }
        const fallbackState = setCustomeowConfigurationState(target, 'fallback', {
          attempts: state.attempts,
          reason: 'timeout',
          retryAt: Date.now() + CUSTOMEOW_CONFIG_RETRY_MS,
        });
        scheduleCustomeowConfigurationRetry(target, fallbackState);
        return false;
      });
      return state.promise;
    }

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = window.setTimeout(function () {
      controller?.abort();
    }, CUSTOMEOW_CONFIG_TIMEOUT_MS);

    state.promise = window
      .fetch(target.url, {
        cache: 'force-cache',
        credentials: 'omit',
        signal: controller?.signal,
      })
      .then(function (response) {
        if (response.status === 404) {
          setCustomeowConfigurationState(target, 'unconfigured', {
            attempts: state.attempts,
            reason: 'not-found',
          });
          return false;
        }
        if (!response.ok) {
          const error = new Error(`Customeow configuration request failed (${response.status})`);
          error.status = response.status;
          throw error;
        }
        return response.json().then(function (data) {
          if (!isValidCustomeowConfiguration(data)) {
            setCustomeowConfigurationState(target, 'unconfigured', {
              attempts: state.attempts,
              reason: 'invalid-configuration',
            });
            return false;
          }
          setCustomeowConfigurationState(target, 'connected', {
            attempts: state.attempts,
            configuration: data,
          });
          return true;
        });
      })
      .catch(function (error) {
        const fallbackState = setCustomeowConfigurationState(target, 'fallback', {
          attempts: state.attempts,
          reason: error?.name === 'AbortError' ? 'timeout' : 'network',
          retryAt: Date.now() + CUSTOMEOW_CONFIG_RETRY_MS,
        });
        scheduleCustomeowConfigurationRetry(target, fallbackState);
        return false;
      })
      .finally(function () {
        window.clearTimeout(timeout);
      });
    state.promise.catch(function () {});
    return state.promise;
  }

  function readCustomeowSkuConnection() {
    const target = getCustomeowConfigurationTarget();
    if (!target) return 'add';
    if (target.isPod && hasRenderedCustomeowConfiguration(target)) {
      const existing = customeowConfigurationStates.get(target.key);
      if (!existing || existing.status !== 'connected') {
        setCustomeowConfigurationState(target, 'connected', { reason: 'pod-config-loaded' });
      }
      return 'customize';
    }
    ensureCustomeowConfiguration(target.variant);
    return customeowConfigurationStates.get(target.key)?.status === 'connected' ? 'customize' : 'add';
  }

  /**
   * True only after Customeow has returned a valid configuration for this SKU.
   * Missing, invalid, slow, or failed requests stay on the safe Add to cart fallback.
   */
  function isSkuConnectedToCustomeow() {
    return readCustomeowSkuConnection() === 'customize';
  }

  function scheduleApplyCustomizeLabel() {
    if (labelApplyTimer) clearTimeout(labelApplyTimer);
    labelApplyTimer = setTimeout(function () {
      labelApplyTimer = null;
      applyCustomizeLabelWhenCustomeowPresent();
    }, 320);
  }

  /**
   * Only yield theme ATC when this SKU is Customeow-connected (Customize Now).
   * Never yield for plain Add to cart — Customeow's ATC is often a dead no-op
   * on products that are not configured (e.g. Ihram).
   */
  function yieldThemeAtcToCustomeow(productInfo) {
    const root = productInfo || getProductInfo() || document;
    if (!isSkuConnectedToCustomeow(root)) return;

    const customeowCta = resolveCustomeowCta(root);
    const ctaLabel = customeowCta
      ? customeowCta.textContent || customeowCta.value || customeowCta.getAttribute?.('aria-label') || ''
      : '';

    // Keep the reliable theme Customize Now button until Customeow has painted a
    // real customization CTA. In particular, never yield to its temporary
    // "Customization not available" state on a slow page.
    if (customeowCta && textLooksLikeCustomize(ctaLabel)) {
      setThemeCustomizePriority(root, false);
      hideThemeAtcForCustomeow(root);
    } else {
      showThemeCustomizeButton(root);
    }
  }

  /** Keep a dependable launcher visible when Customeow has no usable CTA. */
  function showThemeCustomizeButton(productInfo) {
    const root = productInfo || getProductInfo() || document;
    releaseThemeAtcFromCustomeow(root);
    showButtonContainers(root);
    setThemeCustomizePriority(root, true);
    getSubmitButtons(root).forEach(function (btn) {
      btn.style.setProperty('display', 'flex', 'important');
      btn.style.setProperty('visibility', 'visible', 'important');
      btn.style.setProperty('opacity', '1', 'important');
      btn.style.setProperty('width', '100%', 'important');
      btn.style.setProperty('pointer-events', 'auto', 'important');
      btn.style.setProperty('z-index', '6', 'important');
      btn.hidden = false;
      btn.removeAttribute('hidden');
      btn.removeAttribute('aria-hidden');
      if (btn.tabIndex < 0) btn.tabIndex = 0;
      btn.dataset.themeForcedShow = 'true';
      delete btn.dataset.themeHiddenForCustomeow;
    });
    setAtcButtonLabels(true);
  }

  /** Force the theme Add to cart button visible and enabled. */
  function enableThemeAddToCart(productInfo) {
    if (!productInfo) return;
    setThemeCustomizePriority(productInfo, false);
    releaseThemeAtcFromCustomeow(productInfo);
    showButtonContainers(productInfo);
    getSubmitButtons(productInfo).forEach(function (btn) {
      btn.style.setProperty('display', 'flex', 'important');
      btn.style.setProperty('visibility', 'visible', 'important');
      btn.style.setProperty('opacity', '1', 'important');
      btn.style.setProperty('width', '100%', 'important');
      btn.style.setProperty('pointer-events', 'auto', 'important');
      btn.style.setProperty('z-index', '6', 'important');
      btn.hidden = false;
      btn.removeAttribute('hidden');
      btn.removeAttribute('aria-hidden');
      const ownsCartLock =
        btn.dataset.themeCartAddLock === 'true' ||
        btn.closest('form')?.dataset.themeCartAddInFlight === 'true';
      if (!ownsCartLock) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
      if (btn.tabIndex < 0) btn.tabIndex = 0;
      if (btn.tagName === 'BUTTON') btn.setAttribute('type', 'submit');
      if (!btn.getAttribute('name')) btn.setAttribute('name', 'add');
      btn.dataset.themeForcedShow = 'true';
      delete btn.dataset.themeHiddenForCustomeow;
    });
    document.querySelectorAll('sticky-atc [name="add"], sticky-atc .product-form__submit').forEach(function (btn) {
      btn.style.setProperty('display', 'flex', 'important');
      btn.style.setProperty('visibility', 'visible', 'important');
      btn.style.setProperty('pointer-events', 'auto', 'important');
      btn.hidden = false;
      if (btn.dataset.themeCartAddLock !== 'true') {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });
    setAtcButtonLabels(false);
  }

  /**
   * Non-customizable SKU (Ihram, bakhoor, etc.):
   * Always use the theme Add to cart. Customeow may still render an ATC label
   * on unconfigured products, but that button often does nothing.
   */
  function syncNormalAddToCartButton(productInfo) {
    if (!productInfo) return;
    hideCustomeowOverlay(productInfo);
    clearForcedSubmitStyles(productInfo);
    enableThemeAddToCart(productInfo);
  }

  function findProductAddForm(themeBtn, productInfo) {
    return (
      themeBtn?.closest?.('form') ||
      productInfo?.querySelector?.('form[action*="/cart/add"]') ||
      productInfo?.querySelector?.('product-form-component form, form.product-form') ||
      document.querySelector('product-info form[action*="/cart/add"]')
    );
  }

  function submitThemeAddToCart(themeBtn, productInfo) {
    const form = findProductAddForm(themeBtn, productInfo);
    if (!form) return false;

    const formComponent = form.closest('product-form-component');
    if (
      form.dataset.themeCartAddInFlight === 'true' ||
      formComponent?.cartAddInFlight ||
      formComponent?.cartAddPromise
    ) {
      return false;
    }

    // Customeow / delivery locks often leave aria-disabled on the theme button.
    const buttons = [
      themeBtn,
      ...Array.from(form.querySelectorAll('[name="add"], [type="submit"]')),
    ];
    buttons.forEach(function (btn) {
      if (!btn) return;
      if (btn.dataset.themeCartAddLock === 'true' || btn.hasAttribute('data-hadiyah-delivery-disabled')) return;
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('data-hadiyah-delivery-disabled');
    });

    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(themeBtn && form.contains(themeBtn) ? themeBtn : undefined);
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
      return true;
    } catch (e) {
      try {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        return true;
      } catch (err) {
        return false;
      }
    }
  }

  const CUSTOMEOW_OPEN_TIMEOUT_MS = 16000;
  let openCustomeowInFlight = false;
  let pendingCustomeowOpen = null;
  let customeowFinalAction = null;
  let customeowSurfaceLifecycle = null;

  function getCustomeowApps(scope) {
    const root = scope || document;
    const apps = [];
    if (root.matches?.('[data-type="customeowApp"]')) apps.push(root);
    root.querySelectorAll?.('[data-type="customeowApp"]').forEach(function (app) {
      if (!apps.includes(app)) apps.push(app);
    });
    if (root !== document) {
      document.querySelectorAll('[data-type="customeowApp"]').forEach(function (app) {
        if (!apps.includes(app)) apps.push(app);
      });
    }
    return apps;
  }

  function isCustomeowAppLoading(app) {
    return !app || app.classList.contains('customeow-app-skeleton-loading');
  }

  function isCustomeowReady(scope) {
    const readyApps = getCustomeowApps(scope).filter(function (app) {
      return !isCustomeowAppLoading(app) && app.dataset.themeForcedHide !== 'true' && !app.hidden;
    });
    const readyApp = readyApps.find(function (app) {
      return !textLooksLikeUnavailableCustomization(app.textContent || '');
    });
    if (!readyApp) return null;
    const cta = resolveCustomeowCta(scope || readyApp) || resolveCustomeowCta(readyApp);
    if (cta && isElementInteractable(cta)) return cta;
    // In assign mode the app overlay itself is the stable actionable target.
    return isElementInteractable(readyApp) ? readyApp : null;
  }

  function isVisibleSurface(el) {
    if (!el || el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = el.getBoundingClientRect();
    return rect.width >= 180 && rect.height >= 120;
  }

  function looksLikeCustomeowSurface(el) {
    if (!isVisibleSurface(el)) return false;
    if (el.closest?.('[data-hg-pdp-customizer-modal]')) return false;
    if (el.matches?.('[data-cart-addon-drawer], [data-clean-cart-drawer], cart-drawer, quick-add-modal')) return false;
    if (el.closest?.('[data-cart-addon-drawer], [data-clean-cart-drawer], cart-drawer')) return false;

    const identity = `${el.id || ''} ${el.className || ''} ${el.getAttribute?.('data-type') || ''}`.toLowerCase();
    if (identity.includes('customeow') || identity.includes('staticmeow')) return true;

    const text = (el.textContent || '').trim().toLowerCase();
    const hasCustomizationContent =
      el.querySelector?.('[name^="properties["], canvas, [contenteditable="true"]') ||
      text.includes('personalization') ||
      text.includes('personalisation') ||
      text.includes('customization') ||
      text.includes('customisation');
    return Boolean(hasCustomizationContent && (el.getAttribute?.('role') === 'dialog' || el.getAttribute?.('aria-modal') === 'true'));
  }

  function getActiveCustomeowSurface() {
    const candidates = document.querySelectorAll(
      '[role="dialog"], [aria-modal="true"], [class*="customeow" i], [id*="customeow" i], [class*="staticmeow" i]'
    );
    for (let i = candidates.length - 1; i >= 0; i -= 1) {
      if (looksLikeCustomeowSurface(candidates[i])) return candidates[i];
    }
    return null;
  }

  function getCustomeowStatusRegion(productInfo, control) {
    let region = productInfo?.querySelector?.('[data-customeow-open-status]');
    if (region) return region;
    region = document.createElement('div');
    region.className = 'product-form__error-message-wrapper';
    region.dataset.customeowOpenStatus = 'true';
    region.setAttribute('role', 'alert');
    region.setAttribute('aria-live', 'assertive');
    region.hidden = true;
    (control?.closest?.('.add-to-cart-button-container') || control?.parentElement || productInfo)?.appendChild(region);
    return region;
  }

  function showCustomeowStatus(productInfo, control, message) {
    const region = getCustomeowStatusRegion(productInfo, control);
    if (!region) return;
    region.textContent = message || '';
    region.hidden = !message;
  }

  function setCustomeowOpenBusy(control, busy) {
    if (!control) return;
    if (busy) {
      if (control.dataset.customeowOpenBusy === 'true') return;
      control.dataset.customeowOpenBusy = 'true';
      control.dataset.customeowOpenWasDisabled = String(Boolean(control.disabled));
      control.dataset.customeowOpenPreviousAriaDisabled = String(control.getAttribute('aria-disabled') === 'true');
      control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-pressed', 'true');
      control.classList.add('loading');
      control.querySelector('.loading__spinner')?.classList.remove('hidden');
      return;
    }

    if (control.dataset.customeowOpenBusy !== 'true') return;
    control.classList.remove('loading');
    control.querySelector('.loading__spinner')?.classList.add('hidden');
    control.removeAttribute('aria-busy');
    control.removeAttribute('aria-pressed');
    if (control.dataset.customeowOpenWasDisabled !== 'true') control.disabled = false;
    if (control.dataset.customeowOpenPreviousAriaDisabled !== 'true') control.removeAttribute('aria-disabled');
    delete control.dataset.customeowOpenBusy;
    delete control.dataset.customeowOpenWasDisabled;
    delete control.dataset.customeowOpenPreviousAriaDisabled;
  }

  function cleanupPendingCustomeowOpen(options) {
    const pending = pendingCustomeowOpen;
    if (!pending) return;
    pending.observer?.disconnect();
    window.clearInterval(pending.pollTimer);
    window.clearTimeout(pending.timeoutTimer);
    setCustomeowOpenBusy(pending.control, false);
    if (options?.message) showCustomeowStatus(pending.productInfo, pending.control, options.message);
    pendingCustomeowOpen = null;
    openCustomeowInFlight = false;
  }

  function stopCustomeowSurfaceLifecycle() {
    const lifecycle = customeowSurfaceLifecycle;
    if (!lifecycle) return;
    lifecycle.observer?.disconnect();
    window.clearInterval(lifecycle.pollTimer);
    window.clearTimeout(lifecycle.closeIntentTimer);
    customeowSurfaceLifecycle = null;
  }

  function restoreCustomizeButtonAfterEditor(productInfo) {
    const root = productInfo || getProductInfo();
    if (!root) return;
    if (!isSkuConnectedToCustomeow(root)) return;

    showThemeCustomizeButton(root);
    // Let Customeow finish its close animation, then hand back to its CTA only
    // when a genuine, visible Customize control still exists.
    window.setTimeout(function () {
      yieldThemeAtcToCustomeow(root);
    }, 450);
  }

  function trackCustomeowSurfaceLifecycle(surface, productInfo) {
    stopCustomeowSurfaceLifecycle();
    if (!surface) return;

    const lifecycle = {
      surface: surface,
      productInfo: productInfo || getProductInfo(),
      observer: null,
      pollTimer: null,
      closeIntentTimer: null,
    };
    customeowSurfaceLifecycle = lifecycle;

    const checkForClose = function () {
      if (customeowSurfaceLifecycle !== lifecycle) return;
      if (lifecycle.surface.isConnected && isVisibleSurface(lifecycle.surface)) return;
      stopCustomeowSurfaceLifecycle();
      restoreCustomizeButtonAfterEditor(lifecycle.productInfo);
    };

    lifecycle.observer = new MutationObserver(checkForClose);
    lifecycle.observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'],
    });
    lifecycle.pollTimer = window.setInterval(checkForClose, 400);
  }

  function scheduleRestoreAfterCustomeowCloseIntent() {
    const lifecycle = customeowSurfaceLifecycle;
    if (!lifecycle) return;
    window.clearTimeout(lifecycle.closeIntentTimer);
    lifecycle.closeIntentTimer = window.setTimeout(function () {
      if (customeowSurfaceLifecycle !== lifecycle) return;
      stopCustomeowSurfaceLifecycle();
      restoreCustomizeButtonAfterEditor(lifecycle.productInfo);
    }, 350);
  }

  function confirmCustomeowOpened() {
    if (!pendingCustomeowOpen?.activationAttempted) return false;
    const surface = getActiveCustomeowSurface();
    if (!surface) return false;
    if (pendingCustomeowOpen.startingSurface === surface) {
      const baseline = pendingCustomeowOpen.startingSurfaceState;
      const rect = surface.getBoundingClientRect();
      const interactiveCount =
        surface.querySelectorAll?.(
          'input, textarea, select, canvas, [contenteditable="true"], [role="dialog"], [aria-modal="true"]'
        ).length || 0;
      const expanded = baseline && rect.width * rect.height > baseline.area * 1.25;
      const gainedContent = baseline && interactiveCount > baseline.interactiveCount;
      if (!expanded && !gainedContent) return false;
    }
    trackCustomeowSurfaceLifecycle(surface, pendingCustomeowOpen.productInfo);
    cleanupPendingCustomeowOpen();
    return true;
  }

  function tryActivatePendingCustomeow() {
    const pending = pendingCustomeowOpen;
    if (!pending) return false;
    if (getCurrentVariantKey() !== pending.variantKey) {
      cleanupPendingCustomeowOpen();
      return false;
    }
    if (confirmCustomeowOpened()) return true;
    if (pending.activationAttempted) return false;

    const cta = isCustomeowReady(pending.root);
    if (!cta) return false;
    hideThemeAtcForCustomeow(pending.productInfo);
    pending.activationAttempted = true;
    try {
      activateCustomeowOnce(cta);
    } catch (error) {
      cleanupPendingCustomeowOpen({
        message: 'The customizer could not open. Please tap Customize Now to try again.',
      });
      return false;
    }
    return confirmCustomeowOpened();
  }

  function openCustomeowEditor(scope, pointerEvent) {
    if (pendingCustomeowOpen || openCustomeowInFlight) return true;

    const root = scope || getProductInfo() || document;
    const productInfo = root === document ? getProductInfo() : root;
    restoreCustomeowApps(productInfo);

    const control =
      (pointerEvent?.target?.closest?.('[name="add"], button, [role="button"]')?.closest?.('[data-type="customeowApp"]')
        ? getSubmitButtons(productInfo)[0]
        : pointerEvent?.target?.closest?.('[name="add"], button, [role="button"]')) ||
      getSubmitButtons(productInfo)[0];

    showCustomeowStatus(productInfo, control, '');
    setCustomeowOpenBusy(control, true);
    openCustomeowInFlight = true;
    const startingSurface = getActiveCustomeowSurface();
    const startingRect = startingSurface?.getBoundingClientRect?.();
    pendingCustomeowOpen = {
      root: root,
      productInfo: productInfo,
      control: control,
      variantKey: getCurrentVariantKey(),
      startingSurface: startingSurface,
      startingSurfaceState: startingSurface
        ? {
            area: (startingRect?.width || 0) * (startingRect?.height || 0),
            interactiveCount:
              startingSurface.querySelectorAll?.(
                'input, textarea, select, canvas, [contenteditable="true"], [role="dialog"], [aria-modal="true"]'
              ).length || 0,
          }
        : null,
      activationAttempted: false,
      observer: null,
      pollTimer: null,
      timeoutTimer: null,
    };

    const observeRoot = document.body || root;
    pendingCustomeowOpen.observer = new MutationObserver(function () {
      tryActivatePendingCustomeow();
    });
    pendingCustomeowOpen.observer.observe(observeRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'disabled', 'aria-hidden', 'aria-modal'],
    });
    pendingCustomeowOpen.pollTimer = window.setInterval(tryActivatePendingCustomeow, 150);
    pendingCustomeowOpen.timeoutTimer = window.setTimeout(function () {
      cleanupPendingCustomeowOpen({
        message: 'The customizer is taking too long to load. Please tap Customize Now to try again.',
      });
    }, CUSTOMEOW_OPEN_TIMEOUT_MS);

    tryActivatePendingCustomeow();
    return true;
  }

  function scheduleCustomeowInit() {
    if (customeowInitScheduled) return;
    customeowInitScheduled = true;

    [0, 120, 500].forEach(function (delay) {
      const timer = window.setTimeout(function () {
        const variantSelects = getMainVariantSelects();
        if (!variantSelects) return;
        if (productHasNameToggle(variantSelects) && !isWithNameSelected(variantSelects)) return;
        const productInfo = getProductInfo();
        if (productInfo) clearForcedSubmitStyles(productInfo);

        // Only resurrect Customeow when this SKU is connected; otherwise keep ATC clean.
        if (isSkuConnectedToCustomeow(productInfo)) {
          restoreCustomeowApps(productInfo);
          applyCustomizeLabelWhenCustomeowPresent();
          yieldThemeAtcToCustomeow(productInfo);
        } else {
          if (productInfo) syncNormalAddToCartButton(productInfo);
          else setAtcButtonLabels(false);
        }
      }, delay);
      syncTimersLate.push(timer);
    });
  }

  function syncBuyButtonState(withName, productInfo, variant) {
    if (!productInfo) return;

    if (withName) {
      clearForcedSubmitStyles(productInfo);
      showButtonContainers(productInfo);

      if (hasThemePersonalizeDrawer(productInfo)) {
        setAtcButtonLabels(true);
        enableThemeAddToCart(productInfo);
        return;
      }

      if (isSkuConnectedToCustomeow(productInfo)) {
        restoreCustomeowApps(productInfo);
        setAtcButtonLabels(true);
        yieldThemeAtcToCustomeow(productInfo);
      } else {
        // Not connected — plain Add to cart, one click (no Customeow overlay on top).
        syncNormalAddToCartButton(productInfo);
      }
      return;
    }

    hideCustomeowOverlay(productInfo);
    enableThemeAddToCart(productInfo);

    const unavailable = variant?.available === false;
    const productForm = productInfo.querySelector('product-form-component');

    if (productForm?.toggleSubmitButton) {
      productForm.toggleSubmitButton(unavailable, window.variantStrings?.soldOut);
      return;
    }

    getSubmitButtons(productInfo).forEach(function (btn) {
      if (unavailable) {
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  function setAtcButtonLabels(useCustomize) {
    const customizeLabel = 'Customize Now';
    const addLabel =
      (window.variantStrings && window.variantStrings.addToCart) ||
      (window.cartStrings && window.cartStrings.addToCart) ||
      'Add to cart';

    const nodes = document.querySelectorAll(
      '.product-form__buttons [name="add"] .add-to-cart-text__content, .product-form__buttons [name="add"] .add-to-cart-text, sticky-atc [name="add"] .add-to-cart-text, sticky-atc .product-form__submit .add-to-cart-text'
    );

    nodes.forEach(function (el) {
      if (el.querySelector('.loading__spinner, .spinner')) return;
      const current = (el.textContent || '').trim().toLowerCase();
      if (
        current === 'sold out' ||
        current === 'unavailable' ||
        current === 'pre-order' ||
        current === 'preorder' ||
        current.includes('choose')
      ) {
        return;
      }
      const next = useCustomize ? customizeLabel : addLabel;
      if ((el.textContent || '').trim() !== next) {
        // Prefer inner content span when present
        if (el.classList.contains('add-to-cart-text') && el.querySelector('.add-to-cart-text__content')) {
          el.querySelector('.add-to-cart-text__content').textContent = next;
        } else {
          el.textContent = next;
        }
      }
    });
  }

  function hasThemePersonalizeDrawer(scope) {
    try {
      return Boolean(window.PdpPersonalizeDrawer?.isAvailable?.(scope || document));
    } catch (e) {
      return Boolean(document.querySelector('[data-pdp-personalize]'));
    }
  }

  function applyCustomizeLabelWhenCustomeowPresent() {
    const productInfo = getProductInfo();
    if (
      document.documentElement.classList.contains('pdp--without-name') &&
      !isSkuConnectedToCustomeow(productInfo)
    ) {
      if (productInfo) {
        hideCustomeowOverlay(productInfo);
        enableThemeAddToCart(productInfo);
      } else {
        setAtcButtonLabels(false);
      }
      return;
    }

    // Personalizable PDP drawer: always Customize Now on the button (opens slide-in).
    if (hasThemePersonalizeDrawer(productInfo || document)) {
      setAtcButtonLabels(true);
      if (productInfo) {
        clearForcedSubmitStyles(productInfo);
        enableThemeAddToCart(productInfo);
      }
      return;
    }

    const mode = readCustomeowSkuConnection(productInfo);
    const connected = mode === 'customize';
    if (!productInfo) {
      setAtcButtonLabels(connected);
      return;
    }

    if (connected) {
      setAtcButtonLabels(true);
      clearForcedSubmitStyles(productInfo);
      restoreCustomeowApps(productInfo);
      yieldThemeAtcToCustomeow(productInfo);
      return;
    }

    syncNormalAddToCartButton(productInfo);
  }

  function applyNameToggleState(variant) {
    const variantSelects = getMainVariantSelects();
    const productInfo = getProductInfo();
    const resolvedVariant = variant || getSelectedVariantFromDom(variantSelects);
    ensureCustomeowConfiguration(resolvedVariant);

    // Products with Customeow but no With/Without toggle (e.g. hijab hampers / bakhoor)
    if (!productHasNameToggle(variantSelects)) {
      if (productInfo) {
        productInfo.classList.remove('pdp--without-name', 'pdp--with-name', 'pdp-customeow-owns-cta');
        restoreCustomeowApps(productInfo);
        // Skip step-by-step PDPs (ATC stays hidden until the last step).
        if (!productInfo.querySelector('.btn-step-variant, .btn-next__variant, .btn-prev__variant')) {
          if (hasThemePersonalizeDrawer(productInfo)) {
            setAtcButtonLabels(true);
            clearForcedSubmitStyles(productInfo);
            enableThemeAddToCart(productInfo);
          } else if (isSkuConnectedToCustomeow(productInfo)) {
            setAtcButtonLabels(true);
            clearForcedSubmitStyles(productInfo);
            showButtonContainers(productInfo);
            restoreCustomeowApps(productInfo);
            yieldThemeAtcToCustomeow(productInfo);
          } else {
            syncNormalAddToCartButton(productInfo);
          }
        }
      }
      document.documentElement.classList.remove('pdp--without-name', 'pdp--with-name', 'pdp-customeow-owns-cta');
      if (resolvedVariant) {
        updateSkuDisplay(resolvedVariant.sku, productInfo || document);
      }
      return false;
    }

    // A published Customeow configuration is authoritative. Option labels are
    // only a fallback for SKUs that are not connected to Customeow.
    const withName = isSkuConnectedToCustomeow(productInfo) || isWithNameSelected(variantSelects);

    if (productInfo) {
      productInfo.classList.toggle('pdp--with-name', withName);
      productInfo.classList.toggle('pdp--without-name', !withName);
    }

    document.documentElement.classList.toggle('pdp--without-name', !withName);
    document.documentElement.classList.toggle('pdp--with-name', withName);

    if (resolvedVariant) {
      updateSkuDisplay(resolvedVariant.sku, productInfo || document);
    }

    syncBuyButtonState(withName, productInfo, resolvedVariant);
    return true;
  }

  function queueSync(variant) {
    clearTimeout(syncTimer);
    syncTimersLate.forEach(clearTimeout);
    syncTimersLate.length = 0;

    syncTimer = setTimeout(function () {
      applyNameToggleState(variant || null);
    }, 0);

    // One short follow-up after section HTML settles — avoid long multi-pass flicker.
    const timer = setTimeout(function () {
      applyNameToggleState(variant || null);
    }, 120);
    syncTimersLate.push(timer);
  }

  function scheduleInitRetries() {
    if (initRetriesScheduled) return;
    initRetriesScheduled = true;

    [100, 400, 1200].forEach(function (delay) {
      const timer = setTimeout(function () {
        if (!productHasNameToggle(getMainVariantSelects())) return;
        queueSync(getSelectedVariantFromDom(getMainVariantSelects()));
      }, delay);
      syncTimersLate.push(timer);
    });
  }

  function onVariantInteraction(event) {
    // Prefer change events — click on labels fires before the radio updates and caused
    // an extra sync pass that flickered the buy button.
    if (event.type === 'click') return;
    if (!event.target?.closest?.('variant-selects, product-info variant-selects')) return;
    queueSync(null);
  }

  function observeDom() {
    const productInfo = getProductInfo();
    if (!productInfo || productInfo.dataset.nameToggleObserved === 'true') return;
    productInfo.dataset.nameToggleObserved = 'true';

    new MutationObserver(function (mutations) {
      // Ignore Customeow-internal churn — it was causing ATC ↔ Customize flicker.
      const relevant = mutations.some(function (mutation) {
        const t = mutation.target;
        if (!t || !t.closest) return true;
        if (t.closest('[data-type="customeowApp"]')) return false;
        return true;
      });
      if (!relevant) return;
      if (productHasNameToggle(getMainVariantSelects())) {
        queueSync(null);
      }
    }).observe(productInfo, { childList: true, subtree: true });

    // Debounced: catch late-loading Customeow without repeatedly force-showing theme ATC.
    var bodyScanTimer = null;
    function scanForLateNodes() {
      bodyScanTimer = null;
      scheduleApplyCustomizeLabel();
      document.querySelectorAll('[data-type="customeowApp"]').forEach(function (node) {
        if (node.dataset.nameToggleObserved === 'true') return;
        node.dataset.nameToggleObserved = 'true';

        new MutationObserver(function () {
          scheduleApplyCustomizeLabel();
        }).observe(node, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden', 'disabled'],
          childList: true,
          subtree: true,
        });
      });
    }
    new MutationObserver(function () {
      if (bodyScanTimer) return;
      bodyScanTimer = setTimeout(scanForLateNodes, 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    observeDom();
    applyCustomizeLabelWhenCustomeowPresent();

    if (productHasNameToggle(getMainVariantSelects())) {
      queueSync(getSelectedVariantFromDom(getMainVariantSelects()));
      scheduleCustomeowInit();
      return;
    }

    // No name toggle: label from Customeow connection only (not app-shell presence).
    const productInfo = getProductInfo();
    if (productInfo && isSkuConnectedToCustomeow(productInfo)) {
      setAtcButtonLabels(true);
      clearForcedSubmitStyles(productInfo);
      restoreCustomeowApps(productInfo);
      yieldThemeAtcToCustomeow(productInfo);
    } else if (productInfo) {
      syncNormalAddToCartButton(productInfo);
    } else {
      setAtcButtonLabels(false);
    }
    scheduleInitRetries();
    scheduleCustomeowInit();
    [0, 200, 600, 1200].forEach(function (delay) {
      setTimeout(scheduleApplyCustomizeLabel, delay);
    });
  }

  function getCustomeowControlLabel(control) {
    return (control?.textContent || control?.value || control?.getAttribute?.('aria-label') || '')
      .trim()
      .toLowerCase();
  }

  function findCustomeowFinalControl(target) {
    const control = target?.closest?.(
      'button, [role="button"], input[type="button"], input[type="submit"], a[href]'
    );
    if (!control) return null;
    if (control.closest?.('[data-hg-pdp-customizer-modal]')) return null;
    const surface = getActiveCustomeowSurface();
    if (!surface || !surface.contains(control)) return null;

    const label = getCustomeowControlLabel(control);
    const isFinal =
      label.includes('add to cart') ||
      label.includes('add to bag') ||
      label.includes('confirm & add') ||
      label.includes('confirm and add') ||
      label === 'finish' ||
      label === 'done';
    if (!isFinal || textLooksLikeCustomize(label)) return null;
    return { control: control, surface: surface };
  }

  function getFinalActionStatusRegion(action) {
    let region = action.surface?.querySelector?.('[data-customeow-cart-status]');
    if (region) return region;
    region = document.createElement('div');
    region.dataset.customeowCartStatus = 'true';
    region.setAttribute('role', 'alert');
    region.setAttribute('aria-live', 'assertive');
    region.style.cssText = 'margin-top:8px;font-size:14px;line-height:1.4;color:#8a1c11;';
    region.hidden = true;
    (action.control?.parentElement || action.surface)?.appendChild(region);
    return region;
  }

  function showFinalActionError(action, message) {
    if (!action) return;
    const region = getFinalActionStatusRegion(action);
    if (!region) return;
    region.textContent = message || '';
    region.hidden = !message;
  }

  function setFinalActionBusy(action, busy) {
    const control = action?.control;
    if (!control) return;
    if (busy) {
      if (control.dataset.themeCustomeowCartBusy === 'true') return;
      control.dataset.themeCustomeowCartBusy = 'true';
      action.wasDisabled = Boolean(control.disabled);
      action.previousAriaDisabled = control.getAttribute('aria-disabled');
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('aria-busy', 'true');
      control.classList.add('loading', 'is-loading');
      showFinalActionError(action, '');

      queueMicrotask(function () {
        if (customeowFinalAction !== action) return;
        control.disabled = true;
        const isInput = control.tagName === 'INPUT';
        if (isInput) {
          action.previousValue = control.value;
          control.value = 'Adding…';
        } else if (!control.querySelector('[data-theme-customeow-progress], .loading__spinner, [role="progressbar"]')) {
          const progress = document.createElement('span');
          progress.dataset.themeCustomeowProgress = 'true';
          progress.setAttribute('role', 'status');
          progress.textContent = ' Adding…';
          control.appendChild(progress);
        }
      });
      return;
    }

    control.querySelector('[data-theme-customeow-progress]')?.remove();
    if (action.previousValue != null) control.value = action.previousValue;
    control.classList.remove('loading', 'is-loading');
    control.removeAttribute('aria-busy');
    if (!action.wasDisabled) control.disabled = false;
    if (action.previousAriaDisabled == null) control.removeAttribute('aria-disabled');
    else control.setAttribute('aria-disabled', action.previousAriaDisabled);
    delete control.dataset.themeCustomeowCartBusy;
  }

  function clearCustomeowFinalAction(options) {
    const action = customeowFinalAction;
    if (!action) return;
    window.clearTimeout(action.handoffTimer);
    setFinalActionBusy(action, false);
    if (options?.message) showFinalActionError(action, options.message);
    customeowFinalAction = null;
  }

  function closeCustomeowSurfaceAfterSuccess(action) {
    const surface = action?.surface;
    if (!surface || !isVisibleSurface(surface)) return;
    const closeCandidates = surface.querySelectorAll(
      'button[aria-label*="close" i], [role="button"][aria-label*="close" i], button[title*="close" i], [data-action="close"]'
    );
    for (let i = 0; i < closeCandidates.length; i += 1) {
      if (isElementInteractable(closeCandidates[i])) {
        closeCandidates[i].click();
        return;
      }
    }
  }

  function bindCustomeowReliabilityLifecycle() {
    if (window.__hadiyahCustomeowReliabilityBound) return;
    window.__hadiyahCustomeowReliabilityBound = true;

    document.addEventListener(
      'click',
      function (event) {
        const lifecycle = customeowSurfaceLifecycle;
        if (lifecycle?.surface?.contains?.(event.target)) {
          const closeControl = event.target?.closest?.(
            'button, [role="button"], [aria-label], [title], [data-action], [class*="close" i]'
          );
          if (closeControl) {
            const closeText = (closeControl.textContent || '').trim().toLowerCase();
            const closeIdentity = `${closeControl.textContent || ''} ${closeControl.getAttribute?.('aria-label') || ''} ${closeControl.getAttribute?.('title') || ''} ${closeControl.getAttribute?.('data-action') || ''} ${closeControl.className || ''}`
              .trim()
              .toLowerCase();
            if (
              closeIdentity.includes('close') ||
              closeIdentity.includes('cancel') ||
              closeText === '×' ||
              closeText === 'x'
            ) {
              scheduleRestoreAfterCustomeowCloseIntent();
            }
          }
        }

        const loadingApp = event.target?.closest?.('[data-type="customeowApp"]');
        if (loadingApp && isCustomeowAppLoading(loadingApp)) {
          const productInfo = loadingApp.closest('product-info') || getProductInfo();
          const connected = isSkuConnectedToCustomeow(productInfo);
          if (connected) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            openCustomeowEditor(productInfo || document, event);
          }
          return;
        }

        const final = findCustomeowFinalControl(event.target);
        if (!final) return;
        if (customeowFinalAction) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        customeowFinalAction = {
          control: final.control,
          surface: final.surface,
          productForm: null,
          hadError: false,
          wasDisabled: false,
          previousAriaDisabled: null,
          handoffTimer: null,
        };
        setFinalActionBusy(customeowFinalAction, true);
        customeowFinalAction.handoffTimer = window.setTimeout(function () {
          clearCustomeowFinalAction({
            message: 'The item was not added. Please review your customization and try again.',
          });
        }, 20000);
      },
      true
    );

    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape' && customeowSurfaceLifecycle) {
          scheduleRestoreAfterCustomeowCloseIntent();
        }
      },
      true
    );

    document.addEventListener('product-form:cart-add-start', function (event) {
      if (!customeowFinalAction) return;
      window.clearTimeout(customeowFinalAction.handoffTimer);
      customeowFinalAction.productForm = event.target;
    });

    document.addEventListener('product-form:cart-add-success', function (event) {
      const action = customeowFinalAction;
      if (!action || (action.productForm && action.productForm !== event.target)) return;
      closeCustomeowSurfaceAfterSuccess(action);
    });

    document.addEventListener('product-form:cart-add-error', function (event) {
      const action = customeowFinalAction;
      if (!action || (action.productForm && action.productForm !== event.target)) return;
      action.hadError = true;
      action.errorMessage = event.detail?.message || 'Unable to add this item. Please try again.';
      showFinalActionError(action, action.errorMessage);
    });

    document.addEventListener('product-form:cart-add-complete', function (event) {
      const action = customeowFinalAction;
      if (!action || (action.productForm && action.productForm !== event.target)) return;
      clearCustomeowFinalAction(
        action.hadError
          ? { message: action.errorMessage || 'Unable to add this item. Please try again.' }
          : undefined
      );
    });

    document.addEventListener(VARIANT_UPDATE_EVENT, function () {
      cleanupPendingCustomeowOpen();
      stopCustomeowSurfaceLifecycle();
      clearCustomeowFinalAction();
    });
    document.addEventListener('shopify:section:unload', function () {
      cleanupPendingCustomeowOpen();
      stopCustomeowSurfaceLifecycle();
      clearCustomeowFinalAction();
    });
    window.addEventListener('pagehide', function () {
      cleanupPendingCustomeowOpen();
      stopCustomeowSurfaceLifecycle();
      clearCustomeowFinalAction();
    });
  }

  window.PdpNameVariantSync = {
    apply: applyNameToggleState,
    queue: queueSync,
    init: init,
    findCustomeowCta: findCustomeowCta,
    openCustomeowEditor: openCustomeowEditor,
    getActiveCustomeowSurface: getActiveCustomeowSurface,
    yieldThemeAtcToCustomeow: yieldThemeAtcToCustomeow,
    isSkuConnectedToCustomeow: isSkuConnectedToCustomeow,
    getCustomeowConfigurationState: function () {
      const target = getCustomeowConfigurationTarget();
      if (!target) return { status: 'unconfigured', reason: 'no-customeow-server-config' };
      const state = customeowConfigurationStates.get(target.key);
      return {
        status: state?.status || 'checking',
        reason: state?.reason || null,
        sku: target.sku,
        url: target.url || null,
      };
    },
    releaseAtcToCustomeow: function () {
      const productInfo = getProductInfo();
      if (!productInfo) return;
      if (productInfo) clearForcedSubmitStyles(productInfo);
      if (isSkuConnectedToCustomeow(productInfo)) {
        restoreCustomeowApps(productInfo);
        yieldThemeAtcToCustomeow(productInfo);
        setAtcButtonLabels(true);
      } else {
        syncNormalAddToCartButton(productInfo);
      }
    },
  };

  document.addEventListener(VARIANT_UPDATE_EVENT, function (event) {
    cleanupPendingCustomeowOpen();
    queueSync(event?.detail?.resource || null);
  });

  document.addEventListener('customeow-data-updated', function () {
    ensureCustomeowConfiguration(getSelectedVariantFromDom(getMainVariantSelects()));
  });

  window.addEventListener('online', function () {
    const target = getCustomeowConfigurationTarget();
    const state = target && customeowConfigurationStates.get(target.key);
    if (state?.status === 'fallback') ensureCustomeowConfiguration(target.variant, { force: true });
  });

  document.addEventListener('change', onVariantInteraction, true);

  // Before click: for non-customizable SKUs, peel Customeow off the theme ATC.
  document.addEventListener(
    'pointerdown',
    function (event) {
      const productInfo = event.target?.closest?.('product-info') || getProductInfo();
      if (isSkuConnectedToCustomeow(productInfo)) return;

      const themeBtn = event.target?.closest?.(
        '.product-form__buttons [name="add"], sticky-atc [name="add"], sticky-atc .product-form__submit'
      );
      const customeowBtn = event.target?.closest?.(
        '[data-type="customeowApp"] button, [data-type="customeowApp"] a, [data-type="customeowApp"] [role="button"], [data-type="customeowApp"] input[type="button"], [data-type="customeowApp"] input[type="submit"]'
      );
      if (!themeBtn && !customeowBtn) return;

      if (themeBtn) {
        const label = (
          themeBtn.querySelector('.add-to-cart-text__content, .add-to-cart-text')?.textContent ||
          themeBtn.textContent ||
          ''
        )
          .trim()
          .toLowerCase();
        if (label.includes('customiz') || label.includes('customis')) return;
      }

      if (productInfo) syncNormalAddToCartButton(productInfo);
    },
    true
  );

  // Plain Add to cart (theme OR Customeow dead ATC on unconfigured products like Ihram).
  document.addEventListener(
    'click',
    function (event) {
      if (openCustomeowInFlight) return;

      const productInfo = event.target?.closest?.('product-info') || getProductInfo();
      if (isSkuConnectedToCustomeow(productInfo)) return;

      const themeBtn = event.target?.closest?.(
        '.product-form__buttons [name="add"], sticky-atc [name="add"], sticky-atc .product-form__submit'
      );
      const customeowBtn = event.target?.closest?.(
        '[data-type="customeowApp"] button, [data-type="customeowApp"] a, [data-type="customeowApp"] [role="button"], [data-type="customeowApp"] input[type="button"], [data-type="customeowApp"] input[type="submit"]'
      );
      if (!themeBtn && !customeowBtn) return;

      if (themeBtn?.closest?.('[data-type="customeowApp"]')) return;

      if (themeBtn) {
        const label = (
          themeBtn.querySelector('.add-to-cart-text__content, .add-to-cart-text')?.textContent ||
          themeBtn.textContent ||
          ''
        )
          .trim()
          .toLowerCase();
        if (label.includes('customiz') || label.includes('customis')) return;
        if (themeBtn.disabled) return;
      }

      if (customeowBtn) {
        const cmLabel = (customeowBtn.textContent || customeowBtn.value || '').trim().toLowerCase();
        // Only hijack Customeow when it looks like Add to cart / primary buy, not Customize.
        if (cmLabel.includes('customiz') || cmLabel.includes('customis')) return;
      }

      if (window.HadiyahDeliveryGate && !window.HadiyahDeliveryGate.allow(event)) return;

      if (productInfo) syncNormalAddToCartButton(productInfo);

      const submitBtn =
        (productInfo && getSubmitButtons(productInfo)[0]) ||
        themeBtn ||
        document.querySelector('.product-form__buttons [name="add"]');

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      submitThemeAddToCart(submitBtn, productInfo);
    },
    true
  );

  // Capture: only intercept when the visible label is Customize Now for a connected SKU.
  document.addEventListener(
    'click',
    function (event) {
      if (openCustomeowInFlight) return;

      const themeBtn = event.target?.closest?.('.product-form__buttons [name="add"]');
      if (!themeBtn || themeBtn.closest('[data-type="customeowApp"]')) return;
      if (themeBtn.disabled) return;

      const label = (
        themeBtn.querySelector('.add-to-cart-text__content, .add-to-cart-text')?.textContent ||
        themeBtn.textContent ||
        ''
      )
        .trim()
        .toLowerCase();

      if (!label.includes('customiz') && !label.includes('customis')) return;

      const productInfo = themeBtn.closest('product-info') || getProductInfo();
      const productForm = themeBtn.closest('product-form-component');

      // Customeow's final action has already injected completed properties.
      // Let its one theme-button handoff reach the real cart request.
      if (productForm?.hasCustomizationProperties?.()) return;

      // Prefer theme personalization drawer (WhatsApp module) when mounted.
      if (window.PdpPersonalizeDrawer?.isAvailable?.(productInfo || document)) {
        if (window.HadiyahDeliveryGate && !window.HadiyahDeliveryGate.allow(event)) return;
        event.preventDefault();
        event.stopPropagation();
        window.PdpPersonalizeDrawer.open(productInfo || document);
        return;
      }

      if (!isSkuConnectedToCustomeow(productInfo)) return;

      if (window.HadiyahDeliveryGate && !window.HadiyahDeliveryGate.allow(event)) return;

      if (!customeowButtonsBlockReady(productInfo || document) && !resolveCustomeowCta(productInfo || document)) {
        event.preventDefault();
        event.stopPropagation();
        openCustomeowEditor(productInfo || document, event);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openCustomeowEditor(productInfo || document, event);
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      queueSync(null);
    });
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function () {
      queueSync(null);
    });
  }

  bindCustomeowReliabilityLifecycle();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('product-info:loaded', init);
})();
